import { useEffect, useRef, useState } from "react";
import "./TutorDrawer.css";
import imageCompression from "browser-image-compression";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  Button,
  Col,
  Checkbox,
  Form,
  Input,
  Row,
  Select,
  Space,
  Image,
  Tag,
  Rate,
  message,
  Flex,
  notification,
  Alert,
  Modal,
  Tooltip,
  Radio,
  Spin,
} from "antd";
import { useAtom } from "jotai";
import { citysList } from "../states/citys";
import { userRole } from "../states/userRole";
import MyCourses from "./mycourses";
import axios from "../axios";
import Autocomplete from "react-google-autocomplete";
import { useNavigate, useParams } from "react-router-dom";
import TutorHistory from "./tutor-drawer/TutorHistory";
import TutorSubscriptionHistory from "./TutorSubscriptionHistory";
import { ClearOutlined, DeleteOutlined, EditOutlined, InfoCircleOutlined, SaveOutlined } from "@ant-design/icons";

const { Option } = Select;
const teachingTypes = {
  "Looking for Part Time Tuitions": ["Tutor"],
  "Looking for Full time Teaching Job": ["Teacher"],
  Both: ["Tutor", "Teacher"],
};
const profileStatusTagColorMapper = {
  Active: "green",
  InActive: "cyan",
  Block: "red",
  Warning: "yellow",
};

dayjs.extend(utc);
dayjs.extend(timezone);

const TutorDrawer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [citys, _setCitys] = useAtom(citysList);
  const [tutors, setTutors] = useState({});
  const [data, setData] = useState({});
  const [address, setAddress] = useState({});
  const apiKey = (window._env_ && window._env_.REACT_APP_GOOGLE_MAPS_API_KEY) || process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const [tutorDocs, setTutorDocs] = useState({});
  const [activeEdit, setActiveEdit] = useState("");
  const [activeTeachingType, setActiveTeachingType] = useState([]);
  const [profilePicLoad, setProfilePicLoad] = useState(false);
  const [rating, setRating] = useState(null);
  const [fullCourseTemplate, setFullCourseTemplate] = useState([]);
  const [role, setRole] = useAtom(userRole);
  const [paymentDetails, setPaymentDetails] = useState({});
  const [openModal, setOpenModal] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [modalType, setModalType] = useState("");
  const [showTooltip, setShowTooltip] = useState(false);
  const [isPaymentTypeModalOpen, setIsPaymentTypeModalOpen] = useState(false);
  const [selectedPaymentType, setSelectedPaymentType] = useState("Basic");
  const [paymentTypeModalContext, setPaymentTypeModalContext] = useState(""); // "subscribe" or "email"
  // Extract initial address part from the backend data
  const [form] = Form.useForm();

  const [selectedAddress, setSelectedAddress] = useState("");

  // Payment / Bank Details state
  const [bankDetails, setBankDetails] = useState({
    accountHolderName: "",
    bankName: "",
    accountNumber: "",
    ifscCode: "",
    panNumber: "",
    upiId: "",
  });
  const [razorpayLinkedAccountId, setRazorpayLinkedAccountId] = useState(null);
  const [razorpayAccountStatus, setRazorpayAccountStatus] = useState(null);
  const [isEditingBankDetails, setIsEditingBankDetails] = useState(false);
  const [bankDetailsDraft, setBankDetailsDraft] = useState({
    accountHolderName: "",
    bankName: "",
    accountNumber: "",
    ifscCode: "",
    panNumber: "",
    upiId: "",
  });
  const [loadingBankDetails, setLoadingBankDetails] = useState(false);
  const [savingBankDetails, setSavingBankDetails] = useState(false);
  const [creatingRazorpayAccount, setCreatingRazorpayAccount] = useState(false);
  const [earnings, setEarnings] = useState(null);
  const [loadingEarnings, setLoadingEarnings] = useState(false);
  const autoRef = useRef(null);

  // This ensures that the input value is forcibly set when selectedAddress changes
  useEffect(() => {
    if (autoRef.current) {
      autoRef.current.value = selectedAddress;
    }
  }, [selectedAddress]);

  useEffect(() => {
    modifyTutorsListWithUpdatedUserData();
  }, [id]);

  useEffect(() => {
    if (!id) return;
    fetchBankAndRazorpayDetails();
  }, [id]);

  useEffect(() => {
    if (!data?.user_id) return;

    setLoadingEarnings(true);
    axios
      .get(`/v1/cre/tutor/earnings?userId=${data.user_id}`)
      .then((res) => {
        if (res?.data?.is_success) {
          setEarnings(res.data.data || null);
        } else {
          setEarnings(null);
        }
      })
      .catch(() => {
        setEarnings(null);
      })
      .finally(() => {
        setLoadingEarnings(false);
      });
  }, [data?.user_id]);

  const fetchBankAndRazorpayDetails = async () => {
    setLoadingBankDetails(true);
    try {
      const [detailsRes, linkedRes] = await Promise.all([
        axios.get(`/v1/cre/tutor/details-for-transfer`, { params: { tutor_id: id } }),
        axios.get(`/v1/cre/tutor/linked-account`, { params: { tutor_id: id } }),
      ]);
      if (detailsRes.data.is_success) {
        const tutorData = detailsRes.data.data;
        const bank = tutorData?.bank_details || {};
        setBankDetails({
          accountHolderName: bank.accountHolderName || "",
          bankName: bank.bankName || "",
          accountNumber: bank.accountNumber || "",
          ifscCode: bank.ifscCode || "",
          panNumber: bank.panNumber || "",
          upiId: bank.upiId || "",
        });
        setBankDetailsDraft({
          accountHolderName: bank.accountHolderName || "",
          bankName: bank.bankName || "",
          accountNumber: bank.accountNumber || "",
          ifscCode: bank.ifscCode || "",
          panNumber: bank.panNumber || "",
          upiId: bank.upiId || "",
        });
      }
      if (linkedRes.data.data) {
        setRazorpayLinkedAccountId(linkedRes.data.data.accountId || null);
        setRazorpayAccountStatus(linkedRes.data.data.status || null);
      }
    } catch (err) {
      console.error("Failed to fetch bank details", err);
    } finally {
      setLoadingBankDetails(false);
    }
  };

  useEffect(() => {
    fetchAndSetAllCourses();
    setActiveTeachingType((pre) => {
      const courses = [...new Set(data?.courses?.map((course) => course.course_name))].join(" | ");
      let teching_type = [];
      if (data?.teching_type) {
        const teachingTypeValue = teachingTypes[data?.teching_type];
        if (Array.isArray(teachingTypeValue)) {
          teching_type = [...teching_type, ...teachingTypeValue];
        } else if (typeof teachingTypeValue === "string") {
          // Handle comma-separated string values
          teching_type = [...teching_type, ...teachingTypeValue.split(",")];
        }
      }

      if (courses.includes("Special")) teching_type.push("Special Educator");
      if (courses.includes("shadow teacher") || courses.includes("Tuition support"))
        teching_type.push("Shadow Teacher");
      return teching_type;
    });
    fetchPaymentDetails();
  }, [data]);

  useEffect(() => {
    setPaymentDetails({});
    fetchTutorDocs();
    fetchRatingDetails();
    fetchPaymentDetails();
  }, [openModal]);

  useEffect(() => {
    fetchTutorHistory();
  }, []);

  const showSubscribeModal = () => {
    setPaymentTypeModalContext("subscribe");
    setIsPaymentTypeModalOpen(true);
  };

  // Open modal for unsubscribe
  const showUnsubscribeModal = () => {
    setModalType("unsubscribe");
    setOpenModal(true);
  };

  // Handle the action when OK is clicked
  const handleOk = (handleTask) => {
    setConfirmLoading(true);
    handleTask();
  };
  // Handle modal cancellation
  const handleCancel = () => {
    setOpenModal(false);
  };
  const handleTask = () => {
    if (modalType === "subscribe") {
      handleSubscribe();
    } else {
      handleUnSubscribe();
    }
  };

  const [step, setStep] = useState(3);

  const fetchTutorDocs = async () => {
    axios
      .get(`/v1/cre/tutor/docs?id=${id}`)
      .then((response) => {
        setTutorDocs(response.data.data);
      })
      .catch((error) => {
        // console.log(error);
      });
  };

  const fetchRatingDetails = () => {
    axios
      .get(`/v1/cre/tutor/rating/${id}`)
      .then((response) => {
        const { is_success, data, error } = response.data;
        // console.log(is_success, data, error);
        if (!is_success) return;
        setRating(data);
        setActiveEdit("");
      })
      .catch((error) => {
        // console.log(error);
      });
  };

  const verifyStudentDetails = async (userId, response) => {
    setTutors(response);
    setData(response);
    setSelectedAddress(response?.address?.split("<-->")?.pop()?.trim() || "");
    try {
      await axios.post(`/v1/cre/user/verify-payment`, {
        userId: userId,
      });
    } catch (error) {
      console.log(error);
    }
  };

  const modifyTutorsListWithUpdatedUserData = () => {
    try {
      axios
        .get(`/v1/cre/tutor?tutorId=${id}`)
        .then((res) => {
          const response = res?.data?.data?.Result[0];
          verifyStudentDetails(response?.user_id, response);
        })
        .catch((err) => {});
    } catch (error) {}
  };

  const fetchAndSetAllCourses = () => {
    try {
      if (!Object.keys(data)?.length) return;
      axios
        .get(`/v1/cre/tutor/courses?id=${data?.user_id}&tutor_id=${id}`)
        .then((response) => {
          console.log("response of course", response);
          setFullCourseTemplate(response.data.data);
          // modifyTutorsListWithUpdatedUserData();
          setStep(3);
        })
        .catch((err) => {});
    } catch (error) {
      // console.log(error);
    }
  };

  const modifyTutorsListWithUpdatedOtherDetails = (e) => {
    let {
      teching_type,
      cre_comment,
      gender,
      experience,
      have_vehicle,
      is_approved,
      tutor_status,
      profile_status,
      tutor_badge,
      no_tution_a_month,
      plan_type,
    } = e;

    let shouldUpdate = {
      specialTeacher: teching_type.includes("Special Educator"),
      shadowTeacher: teching_type.includes("Shadow Teacher"),
    };

    if (teching_type.includes("Tutor") && teching_type.includes("Teacher")) {
      teching_type = "Both";
    } else if (teching_type.includes("Tutor")) {
      teching_type = "Looking for Part Time Tuitions";
    } else if (teching_type.includes("Teacher")) {
      teching_type = "Looking for Full time Teaching Job";
    } else {
      teching_type = "Looking for Part Time Tuitions";
    }

    axios
      .post("/v1/cre/tutor/other-details", {
        cre_comment,
        gender,
        experience,
        have_vehicle,
        is_approved,
        teching_type,
        tutor_status,
        profile_status,
        tutor_badge,
        no_tution_a_month,
        id: data?.id,
        user_id: data?.user_id,
        plan_type: plan_type === 3 ? null : plan_type,
      })
      .then((res) => {
        // console.log(res.data);
        modifyTutorsListWithUpdatedUserData();
      })
      .catch((err) => {});

    saveTutorCourses(shouldUpdate, true);
    setActiveEdit("");
  };

  const saveTutorCourses = (
    shouldUpdate = {
      specialTeacher: false,
      shadowTeacher: false,
    },
    fromCheckbox = false,
  ) => {
    let { specialTeacher, shadowTeacher } = shouldUpdate;
    let updatedCoursesData = [];
    if (fromCheckbox) {
      fullCourseTemplate.forEach((dropdownForm) => {
        if (!specialTeacher && !shadowTeacher && dropdownForm.category_id === 13) return;
        if (dropdownForm.category_id === 13) {
          let selected_courses = [];
          if (specialTeacher) selected_courses = ["Special educators at home", "Online (Special educator)"];
          if (shadowTeacher) selected_courses = [...selected_courses, "School shadow teacher", "Tuition support"];

          updatedCoursesData.push({
            ...dropdownForm,
            selected_courses,
          });
          return;
        }

        updatedCoursesData.push(dropdownForm);
      });

      updatedCoursesData = updatedCoursesData.filter((dropdownForm) => dropdownForm.selected_courses.length !== 0);
    } else {
      updatedCoursesData = fullCourseTemplate.filter((dropdownForm) => dropdownForm.selected_courses.length !== 0);
    }
    axios
      .post(`/v1/cre/tutor/courses`, {
        userId: data.user_id,
        updatedCoursesData,
      })
      .then((response) => {
        fetchAndSetAllCourses();
      })
      .catch((err) => {
        // console.log(err);
        // console.log("failed to update courses ");
      });
  };

  async function fetchTutorHistory() {
    try {
      const { data } = await axios.get(`/v1/cre/tutor/history`, {
        params: {
          tutor_id: id,
        },
      });
      console.log("The api response data is ", data);

      // setHistory(response.data);
      // setHistory(data.data.result);
      // setEnquiry(data.data.total_enquiry);
      // setConfirmation(data.data.total_confirmation);
    } catch (err) {
      console.log("Error is ", err);
    } finally {
      // setLoading(false);
    }
  }

  const updatePersonalDetails = (data) => {
    axios
      .post("/v1/cre/tutor/personal-details", { ...data })
      .then((res) => {
        modifyTutorsListWithUpdatedUserData();
        setActiveEdit("");
      })
      .catch((err) => {
        // Alert the user with the error message from the API
        alert(err.response?.data?.message || "Failed to update personal details");
      });
  };

  const sendPaymentRecipt = async (paymentId) => {
    try {
      axios
        .post(`/v1/cre/tutor/payment-reciept`, {
          paymentId,
        })
        .then((response) => {
          const { is_success } = response.data;
          if (is_success) {
            notification.success({
              message: "Send Reciept",
              description: "Reciept sent to your email",
              duration: 2,
            });
          }
        });
    } catch (error) {}
  };

  const emailInitialSubcriptionPayment = async (paymentType = "basic") => {
    try {
      const user_id = data?.user_id;
      axios
        .get(`/v1/cre/tutor/payment-invoice/${user_id}`, {
          params: {
            selectedPlan: "tu-pl-499",
            paymentType: paymentType,
          },
        })
        .then((response) => {
          notification.success({
            message: "Send Payment Link",
            description: "Payment link sent to " + data?.email + " email " + user_id,
            duration: 2,
          });
          modifyTutorsListWithUpdatedUserData();
          fetchPaymentDetails();
          setIsPaymentTypeModalOpen(false);
        })
        .catch((error) => {});
    } catch (error) {}
  };

  const handlePaymentTypeModalOk = () => {
    if (paymentTypeModalContext === "subscribe") {
      handleSubscribe(selectedPaymentType);
    } else if (paymentTypeModalContext === "email") {
      emailInitialSubcriptionPayment(selectedPaymentType);
    }
  };

  // ===== Bank & Razorpay Handlers =====
  const handleSaveBankDetails = async () => {
    const { accountHolderName, accountNumber, ifscCode } = bankDetailsDraft;
    if (!accountHolderName || !accountNumber || !ifscCode) {
      notification.error({ message: "Account Holder Name, Account Number, and IFSC Code are required" });
      return;
    }
    setSavingBankDetails(true);
    try {
      const response = await axios.put(`/v1/cre/tutor/${id}/bank-details`, bankDetailsDraft);
      if (response.data.is_success) {
        setBankDetails({ ...bankDetailsDraft });
        setIsEditingBankDetails(false);
        notification.success({ message: "Bank details saved successfully!" });
      } else {
        notification.error({ message: response.data.message || "Failed to save bank details" });
      }
    } catch (err) {
      notification.error({ message: err?.response?.data?.message || "Failed to save bank details" });
    } finally {
      setSavingBankDetails(false);
    }
  };

  const handleCreateRazorpayAccount = async () => {
    setCreatingRazorpayAccount(true);
    try {
      const response = await axios.post(`/v1/cre/tutor/create-linked-account`, { tutor_id: id });
      if (response.data.is_success) {
        const accountId = response.data.data?.id;
        const status = response.data.data?.status;
        setRazorpayLinkedAccountId(accountId);
        setRazorpayAccountStatus(status);
        notification.success({ message: "Razorpay Linked Account Created Successfully!" });
      } else {
        notification.error({ message: response.data.message || "Failed to create Razorpay account" });
      }
    } catch (err) {
      notification.error({ message: err?.response?.data?.message || "Failed to create Razorpay account" });
    } finally {
      setCreatingRazorpayAccount(false);
    }
  };

  const fetchPaymentDetails = async () => {
    try {
      const user_id = data?.user_id;
      axios
        .get(`/v1/cre/user/payment-details/${user_id}`)
        .then((response) => {
          const { data, is_success } = response.data;
          if (is_success) {
            setPaymentDetails(data);
          }
        })
        .catch((error) => {});
    } catch (error) {}
  };

  const onInputClick = (event) => {
    event.target.value = null;
  };

  const ShowDoc = ({ src, onDelete, docType }) => {
    if (!src) return <>Not Uploaded</>;
    const img = ".jpg,.jpeg,.png,.gif,.mp4,.mov,.avi".includes(src.split(".").pop().toLowerCase());

    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
        {img ? (
          <Image width={150} src={src} />
        ) : (
          <a
            href={src}
            target="_blank"
            style={{ display: "flex", justifyContent: "center" }}
            rel="noopener noreferrer"
          >
            show Doc
          </a>
        )}
        <DeleteOutlined
          onClick={() => onDelete(docType)}
          style={{ color: "#ff4d4f", cursor: "pointer", fontSize: 16 }}
          title="Delete document"
        />
      </span>
    );
  };

  const ShowUploadOption = ({ id }) => {
    return (
      <input
        id={id}
        type="file"
        name="myImage"
        onClick={onInputClick}
        accept=".pdf, .doc, .docx, .txt, .ppt, .jpg, .jpeg, .png, .gif"
        onChange={(e) => uploadDoc(e, id)}
        style={{ display: "none" }}
      />
    );
  };

  const uploadedProfilePic = (e) => {
    try {
      let uploadedFile = e.target.files[0];
      if (uploadedFile && uploadedFile.size <= 5 * 1024 * 1024) {
        const options = {
          maxSizeMB: 2,
          maxWidthOrHeight: 1920,
        };

        imageCompression(e.target.files[0], options)
          .then((response) => {
            uploadedFile = new File([response], e.target.files[0].name, {
              lastModified: e.target.files[0].lastModified,
              type: e.target.files[0].type,
            });

            setProfilePicLoad(true);
            const formData = new FormData();

            formData.append("myImage", uploadedFile);
            return axios.post(`v1/cre/tutor/profile-pic?userId=${data?.user_id}`, formData, {
              headers: { "content-type": "multipart/form-data" },
            });
          })
          .then((response) => {
            setProfilePicLoad(false);
            modifyTutorsListWithUpdatedUserData();
          })
          .catch((error) => {
            setProfilePicLoad(false);
            alert("Error While Uploading Profile Pic");
          });
      } else {
        alert("Image size should be less than or equal to 5MB");
      }
    } catch (error) {
      alert("Error While Uploading Profile Pic");
    }
  };

  const uploadDoc = (e, docType) => {
    try {
      const uploadedFile = e.target.files[0];
      if (
        !".pdf, .doc, .docx, .txt, .ppt, .jpg, .jpeg, .png, .gif".includes(uploadedFile.name.split(".").slice(-1)[0])
      ) {
        alert("only .pdf, .doc, .docx, .txt, .ppt, .jpg, .jpeg, .png, .gif format files are allowed");
        return;
      }
      if (uploadedFile && uploadedFile.size <= 10 * 1024 * 1024) {
        const formData = new FormData();
        formData.append("myImage", uploadedFile);
        axios
          .post(`v1/cre/tutor/doc?userId=${data?.user_id}`, formData, {
            headers: {
              "content-type": "multipart/form-data",
              "doc-type": docType,
            },
          })
          .then((res) => {
            fetchTutorDocs();
          })
          .catch((error) => {
            alert("Error While Uploading Document");
          });
      } else {
        alert("Document size should be less than or equal to 10 MB");
      }
    } catch (error) {
      alert("Error While Uploading Document");
    }
  };

  const deleteDoc = (docType) => {
    Modal.confirm({
      title: "Delete Document",
      content: "Are you sure you want to delete this document?",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: () => {
        axios
          .delete(`/v1/cre/tutor/doc?userId=${data?.user_id}&docType=${docType}`)
          .then(() => {
            fetchTutorDocs();
            message.success("Document deleted successfully");
          })
          .catch(() => {
            message.error("Error while deleting document");
          });
      },
    });
  };

  const handleSubscribe = (paymentType) => {
    const user_id = data?.user_id;
    console.log("user_id---- handle subscribe func called", user_id);
    setConfirmLoading(true);
    axios
      .post(`/v1/cre/mark-as-paid/`, {
        user_id: user_id.toString(),
        paymentType: paymentType,
      })
      .then((response) => {
        if (response && response?.data?.is_success) {
          notification.success({
            message: "",
            description: response?.data?.data,
            duration: 2,
          });
        } else {
          notification.error({
            message: "",
            description: response?.data?.data || "",
            duration: 2,
          });
        }
        modifyTutorsListWithUpdatedUserData();
        setConfirmLoading(false);
        setIsPaymentTypeModalOpen(false);
      })
      .catch((error) => {
        setConfirmLoading(false);
        setIsPaymentTypeModalOpen(false);
      });
  };

  const handleUnSubscribe = () => {
    const user_id = data?.user_id;
    axios
      .post(`/v1/cre/unsubscribe/`, {
        user_id: user_id.toString(),
      })
      .then((response) => {
        if (response && response?.data?.is_success) {
          notification.success({
            message: "",
            description: response?.data?.data,
            duration: 2,
          });
          modifyTutorsListWithUpdatedUserData();
        }
      })
      .catch((error) => {})
      .finally(() => {
        handleCancel();
        setConfirmLoading(false);
      });
  };

  const getPlanType = (planType) => {
    if (planType === 1) return "Basic";
    if (planType === 2) return "Premium";
    return "N/A";
  };

  const formatINR = (value) => `₹${(Number(value) || 0).toLocaleString("en-IN")}`;

  const earningsSummary = {
    total: earnings?.total ?? earnings?.total_earnings ?? 0,
    lastMonth: earnings?.last_month ?? earnings?.lastMonth ?? 0,
  };

  return (
    <>
      <div className="tail-p-6 tail-max-w-screen-xl tail-mx-auto tail-min-h-screen tail-bg-white">
        <div className="tail-text-xl tail-font-bold tail-text-gray-800">
          {`Account Details of ${data?.id} - ${data?.user_id}`}
        </div>
        {/* Profile Header Section */}
        <section className="edit-pannel-profile-section-wrapper">
          <div className="approved-icon">
            <Image height={100} src={data?.profile_pic_link} fallback="/person-icon.png" />
            {data?.is_approved ? (
              <div
                style={{
                  backgroundColor: "rgba(44, 153, 27, 1)",
                  borderRadius: "50%",
                  width: "20px",
                  height: "20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginLeft: "7px",
                  position: "absolute",
                  right: "3px",
                  bottom: "25px",
                }}
              >
                <img src="/approved-icon.svg" alt="tick mark" />
              </div>
            ) : (
              <></>
            )}
            <div className="showRequest--Edit-btn">
              {/* {console.log(data?.profile_pic_link)} */}
              {!profilePicLoad ? (
                <form>
                  <label htmlFor="profilePic-1">File Upload</label>
                  <input
                    id="profilePic-1"
                    type="file"
                    name="myImage"
                    accept=".jpg, .jpeg, .png, .gif"
                    onChange={uploadedProfilePic}
                    onClick={onInputClick}
                    style={{ display: "none" }}
                  />
                </form>
              ) : (
                <form>
                  <label>Uploading</label>
                </form>
              )}
            </div>
          </div>

          <div className="name-place-status-wrapper">
            <div className="fullname-title">
              {data?.first_name} {data?.last_name}
            </div>
            <div className="place-gender-title">
              {citys[data?.city_id]}
              {" - "}
              {data?.gender}
            </div>
            <div className="active-status-title">
              {rating && (
                <Rate
                  allowHalf
                  defaultValue={
                    Object.values(rating).reduce((acc, cur) => acc + parseFloat(cur), 0) / Object.keys(rating).length
                  }
                  disabled={true}
                />
              )}
              <Tag
                className="active-status-value"
                color={profileStatusTagColorMapper[data?.profile_status ? data?.profile_status : "Active"]}
              >
                {data?.profile_status ? data?.profile_status : "Active"}
              </Tag>
            </div>
          </div>
        </section>

        {/* ---------------------------------------- personal details start -------------------------------- */}

        <section className="section-title-edit-wrapper">
          <div className="section-title-container">
            Personal Details
            <div
              style={{
                backgroundColor: "rgba(44, 153, 27, 1)",
                borderRadius: "50%",
                width: "20px",
                height: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginLeft: "7px",
              }}
            >
              <img src="/white-tick.svg" alt="tick mark" />
            </div>
          </div>

          {!activeEdit && (
            <div className="tail-flex tail-items-end tail-gap-2">
              <Button
                type="primary"
                className="tail-mt-1"
                icon={<EditOutlined />}
                iconPosition="end"
                size="medium"
                onClick={() => {
                  setActiveEdit("personal-details");
                }}
              >
                Edit
              </Button>
            </div>
          )}
        </section>

        {activeEdit !== "personal-details" && (
          <section>
            <div className="profile-section-personal-details-titles-wrapper">
              <span className="profile-section-title">First Name : </span>
              <span className="profile-section-value">{data?.first_name}</span>
            </div>
            <div className="profile-section-personal-details-titles-wrapper">
              <span className="profile-section-title">Last Name : </span>
              <span className="profile-section-value">{data?.last_name}</span>
            </div>
            <div className="profile-section-personal-details-titles-wrapper">
              <span className="profile-section-title">Phone : </span>
              <span className="profile-section-value">{data?.phone}</span>
            </div>
            <div className="profile-section-personal-details-titles-wrapper">
              <span className="profile-section-title">Alt Phone : </span>
              <span className="profile-section-value">{data?.phone2}</span>
            </div>
            <div className="profile-section-personal-details-titles-wrapper">
              <span className="profile-section-title">Email : </span>
              <span className="profile-section-value">{data?.email}</span>
            </div>

            <div className="profile-section-personal-details-titles-wrapper">
              <span className="profile-section-title">Address: </span>
              <span className="profile-section-value">
                {data?.address
                  ?.split("<-->")
                  .map((area) => {
                    if (area != "undefined") return area;
                    return "";
                  })
                  .join(" ")}
              </span>
            </div>
            <div className="profile-section-personal-details-titles-wrapper">
              <span className="profile-section-title">City : </span>
              <span className="profile-section-value">{citys[data?.city_id]}</span>
            </div>
            <div className="profile-section-personal-details-titles-wrapper">
              <span className="profile-section-title">Last Login : </span>
              <span className="profile-section-value">
                {data?.last_login_at
                  ? dayjs.utc(data.last_login_at).tz("Asia/Kolkata").format("DD MMM YYYY, hh:mm A")
                  : "Never"}
              </span>
            </div>
          </section>
        )}
        {activeEdit === "personal-details" && (
          <Form
            form={form}
            layout="vertical"
            onFinish={(e) => {
              const oldAddress = data.address.split("<-->");
              const updatedData = {
                userId: data.user_id,
                first_name: data.first_name,
                last_name: data.last_name,
                phone: data.phone,
                phone2: data.phone2,
                email: data.email,
                flat: oldAddress[0],
                apartment: oldAddress[1],
                street: oldAddress[2],
                city_name: data.city_name,
                locality: oldAddress[3],
                zip: data.zip,
                latitude: data.latitude,
                longitude: data.longitude,
                user_type: "tutor",
                ...e,
                ...address,
              };
              updatePersonalDetails(updatedData);
            }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="first_name"
                  label="First Name"
                  className="profile-section-title"
                  initialValue={data?.first_name}
                  rules={[
                    {
                      required: true,
                      message: "Please enter user first name",
                    },
                  ]}
                >
                  <Input placeholder="Please enter user first name" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="last_name"
                  label="Last Name"
                  className="profile-section-title"
                  initialValue={data?.last_name}
                  rules={[
                    {
                      required: true,
                      message: "Please enter user last name",
                    },
                  ]}
                >
                  <Input placeholder="Please enter user last name" />
                </Form.Item>
              </Col>
              {/* <Col span={12}>
                <Form.Item
                  name="phone"
                  label="Phone"
                  initialValue={data?.phone}
                  className="profile-section-title"
                  rules={[
                    {
                      required: true,
                      message: "Please enter user phone no",
                    },
                  ]}
                >
                  <Input placeholder="Please enter user phone no" />
                </Form.Item>
              </Col> */}
              <Col span={12}>
                <Form.Item
                  name="phone2"
                  label="Alter Phone"
                  className="profile-section-title"
                  initialValue={data?.phone2}
                  rules={[
                    {
                      // required: true,
                      message: "Please enter user alter phone no",
                    },
                  ]}
                >
                  <Input placeholder="Please enter user alter phone no" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="email"
                  label="Email"
                  className="profile-section-title"
                  rules={[
                    {
                      required: true,
                      message: "Please enter user email",
                    },
                  ]}
                  initialValue={data?.email}
                >
                  <Input placeholder="Please enter user email" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="flat"
                  label="Flat No"
                  className="profile-section-title"
                  rules={[
                    {
                      required: false,
                      message: "Please enter user flat no",
                    },
                  ]}
                  initialValue={data?.address.split("<-->")[0]}
                >
                  <Input placeholder="Please enter user flat no" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="apartment"
                  label="Apartment Name"
                  className="profile-section-title"
                  rules={[
                    {
                      required: false,
                      message: "Please enter user apartment name ",
                    },
                  ]}
                  initialValue={data?.address.split("<-->")[1]}
                >
                  <Input placeholder="Please enter user apartment name" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="street"
                  label="Street Name"
                  className="profile-section-title"
                  rules={[
                    {
                      required: false,
                      message: "Please enter user street name",
                    },
                  ]}
                  initialValue={data?.address.split("<-->")[2]}
                >
                  <Input placeholder="Please enter user street name" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="city_name"
                  label="City"
                  className="profile-section-title"
                  rules={[
                    {
                      required: true,
                      message: "Please enter user city",
                    },
                  ]}
                  // initialValue={data?.city_name}
                  initialValue={citys[data?.city_id]}
                >
                  <Select placeholder="Please enter user city" showSearch>
                    {Object.keys(citys).map((city_id) => (
                      <Option key={city_id} value={citys[city_id]}>
                        {citys[city_id]}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="full_address"
                  label={
                    <span>
                      Address{" "}
                      <Tooltip
                        title="Start typing and select the suggested address"
                        open={showTooltip}
                        onOpenChange={(visible) => setShowTooltip(visible)}
                      >
                        <InfoCircleOutlined style={{ color: "#1890ff", cursor: "pointer" }} />
                      </Tooltip>
                    </span>
                  }
                  className="profile-section-title"
                  rules={[
                    {
                      // required: true,
                      message: "Please enter user address",
                    },
                  ]}
                >
                  <Autocomplete
                    apiKey={apiKey}
                    ref={autoRef}
                    placeholder="Please enter user pincode and select address"
                    className="ant-input css-dev-only-do-not-override-1uweeqc ant-input-outlined"
                    style={{
                      width: "350px",
                      padding: "5px",
                      border: "1px solid black",
                    }}
                    // Controlled input
                    value={selectedAddress}
                    // Manual typing updates
                    onChange={(e) => {
                      const val = e.target.value;
                      setSelectedAddress(val);
                      form.setFieldsValue({ full_address: val });
                    }}
                    onFocus={() => setShowTooltip(true)}
                    onBlur={() => setShowTooltip(false)}
                    // Autocomplete selection
                    onPlaceSelected={(place) => {
                      let addressComponents = place.address_components || [];

                      let pinCode = addressComponents.find((c) => c.types.includes("postal_code"));

                      let locality = addressComponents.map((geo) => geo.long_name).join(", ");

                      let latitude = place.geometry.location.lat();
                      let longitude = place.geometry.location.lng();

                      const formattedAddress = `${pinCode?.short_name || ""}, ${locality}`;

                      // Update input value
                      setSelectedAddress(formattedAddress);

                      // Update form value
                      form.setFieldsValue({ full_address: formattedAddress });

                      // Update address state for submission
                      setAddress({
                        zip: pinCode?.short_name || "",
                        locality: locality,
                        latitude,
                        longitude,
                        gmap_meta_data: JSON.stringify(addressComponents),
                      });
                    }}
                    options={{
                      types: [],
                      componentRestrictions: { country: "IN" },
                    }}
                    defaultValue={data?.address?.split("<-->")?.pop()?.trim() || ""}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item
              wrapperCol={{
                offset: 8,
                span: 16,
              }}
            >
              <Space>
                <Button type="primary" htmlType="submit">
                  Submit
                </Button>
                <Button
                  htmlType="button"
                  onClick={() => {
                    setActiveEdit("");
                  }}
                >
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        )}

        {/* ---------------------------------------- personal details end -------------------------------- */}
        {/* ---------------------------------------- Rating details start-------------------------------- */}

        <section className="section-title-edit-wrapper">
          <div className="section-title-container">
            Rating Details
            <div
              style={{
                backgroundColor: "rgba(44, 153, 27, 1)",
                borderRadius: "50%",
                width: "20px",
                height: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginLeft: "7px",
              }}
            >
              <img src="/white-tick.svg" alt="tick mark" />
            </div>
          </div>

          {!activeEdit && (
            <div className="tail-flex tail-items-end tail-gap-2">
              <Button
                type="primary"
                className="tail-mt-1"
                icon={<EditOutlined />}
                iconPosition="end"
                size="medium"
                onClick={() => {
                  setActiveEdit("rating-details");
                }}
              >
                Edit
              </Button>
            </div>
          )}
        </section>

        {activeEdit !== "rating-details" && rating && (
          <Flex wrap="wrap" className="rating-details-section">
            <div className="profile-section-personal-details-titles-wrapper">
              <span className="profile-section-title">personality Rating : </span>
              <span className="profile-section-title">
                <Rate allowHalf defaultValue={parseFloat(rating?.personal)} disabled={true} />
              </span>
            </div>

            {/* <div className="profile-section-personal-details-titles-wrapper">
              <span className="profile-section-title">behaviour Rating : </span>
              <span className="profile-section-title">
                <Rate
                  allowHalf
                  defaultValue={parseFloat(rating?.behaviour)}
                  disabled={true}
                />
              </span>
            </div> */}

            <div className="profile-section-personal-details-titles-wrapper">
              <span className="profile-section-title">subject Rating : </span>
              <span className="profile-section-title">
                <Rate allowHalf defaultValue={parseFloat(rating?.subject)} disabled={true} />
              </span>
            </div>

            <div className="profile-section-personal-details-titles-wrapper">
              <span className="profile-section-title">communication Rating : </span>
              <span className="profile-section-title">
                <Rate allowHalf defaultValue={parseFloat(rating?.communication)} disabled={true} />
              </span>
            </div>

            <div className="profile-section-personal-details-titles-wrapper">
              <span className="profile-section-title">trustable Rating : </span>
              <span className="profile-section-title">
                <Rate allowHalf defaultValue={parseFloat(rating?.trustable)} disabled={true} />
              </span>
            </div>
          </Flex>
        )}

        {activeEdit === "rating-details" && (
          <Form
            requiredMark={false}
            name="basic"
            initialValues={{
              rating: data?.rating,
            }}
            onFinish={(values) => {
              axios
                .put(`/v1/cre/tutor/rating/${id}`, values)
                .then((res) => {
                  const { is_success, data, error } = res.data;
                  // console.log(res.data);
                  if (is_success) {
                    message.success("Rating updated successfully");
                    setActiveEdit("");
                    setRating(null);
                    fetchRatingDetails();
                  } else {
                    // console.log(error);
                  }
                })
                .catch((err) => {
                  // console.log(err);
                });
              setActiveEdit("");
            }}
          >
            <Row gutter={16}>
              <Col span={25}>
                <Form.Item
                  name="personal"
                  label="Personality Rating"
                  rules={[
                    {
                      required: true,
                      message: "Please enter personal rating",
                    },
                  ]}
                  initialValue={parseFloat(rating.personal)}
                >
                  <Rate allowHalf defaultValue={rating.personal} />
                </Form.Item>
              </Col>

              {/* <Col span={25}>
                <Form.Item
                  name="behaviour"
                  label="Behaviour Rating"
                  rules={[
                    {
                      required: true,
                      message: "Please enter behaviour rating",
                    },
                  ]}
                  initialValue={parseFloat(rating.behaviour)}
                >
                  <Rate allowHalf defaultValue={rating.behaviour} />
                </Form.Item>
              </Col> */}

              <Col span={25}>
                <Form.Item
                  name="subject"
                  label="Subject Rating"
                  rules={[
                    {
                      required: true,
                      message: "Please enter subject rating",
                    },
                  ]}
                  initialValue={parseFloat(rating.subject)}
                >
                  <Rate allowHalf defaultValue={rating.subject} />
                </Form.Item>
              </Col>

              <Col span={25}>
                <Form.Item
                  name="communication"
                  label="Communication Rating"
                  rules={[
                    {
                      required: true,
                      message: "Please enter communication rating",
                    },
                  ]}
                  initialValue={parseFloat(rating.communication)}
                >
                  <Rate allowHalf defaultValue={rating.communication} />
                </Form.Item>
              </Col>

              <Col span={25}>
                <Form.Item
                  name="trustable"
                  label="Trustable Rating"
                  rules={[
                    {
                      required: true,
                      message: "Please enter trustable rating",
                    },
                  ]}
                  initialValue={parseFloat(rating.trustable)}
                >
                  <Rate allowHalf defaultValue={rating.trustable} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              wrapperCol={{
                offset: 8,
                span: 16,
              }}
            >
              <Space>
                <Button type="primary" htmlType="submit">
                  Submit
                </Button>
                <Button
                  htmlType="button"
                  onClick={() => {
                    setActiveEdit("");
                  }}
                >
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        )}

        {/* ---------------------------------------- Rating details end-------------------------------- */}
        {/* ---------------------------------------- other details start-------------------------------- */}

        <section className="section-title-edit-wrapper">
          <div className="section-title-container">
            Other Details
            <div
              style={{
                backgroundColor: "rgba(44, 153, 27, 1)",
                borderRadius: "50%",
                width: "20px",
                height: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginLeft: "7px",
              }}
            >
              <img src="/white-tick.svg" alt="tick mark" />
            </div>
          </div>
          {!activeEdit && (
            <div className="tail-flex tail-items-end tail-gap-2">
              <Button
                type="primary"
                className="tail-mt-1"
                icon={<EditOutlined />}
                iconPosition="end"
                size="medium"
                onClick={() => {
                  setActiveEdit("other-details");
                }}
              >
                Edit
              </Button>
            </div>
          )}
        </section>

        {activeEdit !== "other-details" && (
          <section>
            <div className="profile-section-personal-details-titles-wrapper">
              <span className="profile-section-title">Current Status : </span>
              <span className="profile-section-value">{data?.tutor_status}</span>
            </div>
            <div className="profile-section-personal-details-titles-wrapper">
              <span className="profile-section-title">Terms &amp; Conditions : </span>
              <span className="profile-section-value">
                {data?.terms_accepted_at ? (
                  <Tag color="green">
                    Accepted (
                    {new Date(data.terms_accepted_at).toLocaleString("en-IN", {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })}
                    )
                  </Tag>
                ) : (
                  <Tag color="red">Not Accepted</Tag>
                )}
              </span>
            </div>
            <div className="profile-section-personal-details-titles-wrapper">
              <span className="profile-section-title">Plan Type : </span>
              <span className="profile-section-value">{getPlanType(data?.plan_type)}</span>
            </div>
            <div className="profile-section-personal-details-titles-wrapper">
              <span className="profile-section-title">Active Status : </span>
              <span className="profile-section-value">{data?.profile_status}</span>
            </div>
            {/* <div className="profile-section-personal-details-titles-wrapper">
              <span className="profile-section-title">Rating : </span>
              <span className="profile-section-value">
                <Rate
                  allowHalf
                  defaultValue={parseFloat(data?.no_tution_a_month)}
                  disabled={true}
                />
              </span>
            </div> */}
            <div className="profile-section-personal-details-titles-wrapper">
              <span className="profile-section-title">Experience : </span>
              <span className="profile-section-value">{data?.experience}</span>
            </div>
            <div className="profile-section-personal-details-titles-wrapper">
              <span className="profile-section-title">Gender : </span>
              <span className="profile-section-value">{data?.gender}</span>
            </div>
            <div className="profile-section-personal-details-titles-wrapper">
              <span className="profile-section-title">Teaching Type : </span>
              <span className="profile-section-value">{activeTeachingType.join(" | ")}</span>
            </div>
            <div className="profile-section-personal-details-titles-wrapper">
              <span className="profile-section-title">Have Vehicle : </span>
              <span className="profile-section-value">{data?.have_vehicle === 1 ? "Yes " : "No"}</span>
            </div>
            <div className="profile-section-personal-details-titles-wrapper">
              <span className="profile-section-title">Verify Status : </span>
              <span className="profile-section-value">{data?.is_approved === 1 ? "Verified" : "Not Verified"}</span>
            </div>
            <div className="profile-section-personal-details-titles-wrapper">
              <span className="profile-section-title">Qualification : </span>
              <span className="profile-section-value">{data?.qualification ?? ""}</span>
            </div>
            <div className="profile-section-personal-details-titles-wrapper">
              <span className="profile-section-title">Reference : </span>
              <span className="profile-section-value">{data?.reference ?? ""}</span>
            </div>
            <Alert
              style={{ margin: "20px 0" }}
              message={
                paymentDetails?.subscriptionData?.subscription_status === "ACTIVE"
                  ? "Subscription Status: Success"
                  : `Subscription Status: Pending - Email sent count: ${
                      paymentDetails?.subscriptionData?.invoice_sent_count || 0
                    }`
              }
              type={paymentDetails?.subscriptionData?.subscription_status === "ACTIVE" ? "success" : "error"}
              showIcon
              action={
                paymentDetails?.subscriptionData?.status_code === "SUCCESS" ? (
                  <Button
                    type="primary"
                    onClick={() => {
                      sendPaymentRecipt(paymentDetails?.subscriptionData?.payment_id);
                    }}
                  >
                    Send Reciept
                  </Button>
                ) : (
                  <Button
                    type="primary"
                    onClick={() => {
                      // send email for payment
                      setPaymentTypeModalContext("email");
                      setIsPaymentTypeModalOpen(true);
                    }}
                  >
                    Email Payment Link
                  </Button>
                )
              }
            />
            {(role === "Superadmin" || role === "Admin") && (
              <Flex justify="flex-end" gap="small">
                {paymentDetails && paymentDetails?.subscriptionData?.subscription_status === "ACTIVE" ? (
                  <Button type="primary" onClick={showUnsubscribeModal}>
                    Unsubscribe
                  </Button>
                ) : (
                  <Button type="primary" onClick={showSubscribeModal}>
                    Subscribe
                  </Button>
                )}
              </Flex>
            )}

            <Modal
              title={modalType === "subscribe" ? "Subscribe" : "Unsubscribe"}
              open={openModal}
              onCancel={handleCancel}
              footer={[
                <Button key="back" onClick={handleCancel}>
                  Cancel
                </Button>,
                <Button key="submit" type="primary" loading={confirmLoading} onClick={() => handleOk(handleTask)}>
                  Confirm
                </Button>,
              ]}
            >
              <p>
                {modalType === "subscribe"
                  ? "Are you sure you want to Subscribe?"
                  : "Are you sure you want to Unsubscribe?"}
              </p>
            </Modal>
            <div className="profile-section-personal-details-titles-wrapper">
              <span className="profile-section-title">Comment : </span>{" "}
              <span className="profile-section-value">{data?.cre_comment}</span>
            </div>
          </section>
        )}

        {activeEdit === "other-details" && (
          <Form
            layout="vertical"
            // hideRequiredMark
            onFinish={(e) => {
              // console.log(e);
              modifyTutorsListWithUpdatedOtherDetails(e);
            }}
          >
            {/* <Row gutter={16}>
              <Col span={25}>
                <Form.Item
                  name="no_tution_a_month"
                  label="Tutor Rating"
                  className="profile-section-title"
                  rules={[
                    {
                      required: false,
                      message: "Please give tutor rating",
                    },
                  ]}
                  initialValue={parseFloat(data?.no_tution_a_month)}
                >
                  <Rate
                    allowHalf
                    defaultValue={parseFloat(data?.no_tution_a_month)}
                  />
                </Form.Item>
              </Col>
            </Row> */}
            <Row gutter={16}>
              <Col span={25}>
                <Form.Item
                  name="teching_type"
                  label="Teaching Type"
                  className="profile-section-title"
                  rules={[
                    {
                      required: false,
                      message: "Please select teaching type",
                    },
                  ]}
                  initialValue={activeTeachingType}
                >
                  <Checkbox.Group defaultValue={activeTeachingType}>
                    <Space direction="">
                      <Checkbox value="Tutor">Tutor</Checkbox>
                      <Checkbox value="Teacher">Teacher</Checkbox>
                      <Checkbox value="Special Educator">Special Educator</Checkbox>
                      <Checkbox value="Shadow Teacher">Shadow Teacher</Checkbox>
                    </Space>
                  </Checkbox.Group>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="tutor_status"
                  label="Current Status"
                  className="profile-section-title"
                  rules={[
                    {
                      required: true,
                      message: "Please choose Current status ",
                    },
                  ]}
                  initialValue={data?.tutor_status}
                >
                  <Select placeholder="Please choose Current status" initialValue={data?.tutor_status}>
                    <Option value={"New"}>New</Option>
                    <Option value={"Interested"}>Interested</Option>
                    <Option value={"Doc_Done"}>Doc Done</Option>
                    <Option value={"Not_Interested"}>Not Interested</Option>
                    {role !== "Superviser" && (
                      <>
                        <Option value={"Payment_Done"}>Payment Done</Option>
                        <Option value={"Verified"}>Verified</Option>
                        <Option value={"Expired"}>Expired</Option>
                      </>
                    )}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="plan_type"
                  label="Plan Type"
                  className="profile-section-title"
                  rules={[
                    {
                      required: true,
                      message: "Please choose Plan Type ",
                    },
                  ]}
                  initialValue={data?.is_paid ? data?.plan_type : null}
                >
                  <Select placeholder="Please choose Plan Type" initialValue={data?.plan_type}>
                    <Option value={3}>Not Paid</Option>
                    <Option value={1}>Basic</Option>
                    <Option value={2}>Premium</Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  name="profile_status"
                  label="Active Status"
                  className="profile-section-title"
                  rules={[
                    {
                      required: true,
                      message: "Please choose active status ",
                    },
                  ]}
                  initialValue={data?.profile_status}
                >
                  <Select placeholder="Please choose active status" initialValue={data?.profile_status}>
                    <Option value={"Active"}>Active</Option>
                    <Option value={"InActive"}>In Active</Option>
                    <Option value={"Block"}>Block</Option>
                    <Option value={"Warning"}>Warning</Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  name="experience"
                  label="Experience"
                  className="profile-section-title"
                  rules={[
                    {
                      required: true,
                      message: "Please select Experience",
                    },
                  ]}
                  initialValue={data?.experience}
                >
                  <Select initialValue={data?.experience} placeholder="Please select Experience">
                    <Option value="Fresher">Fresher</Option>
                    <Option value="1 year">1 year</Option>
                    <Option value="2 years">2 years</Option>
                    <Option value="3 years">3 years</Option>
                    <Option value="4 years">4 years</Option>
                    <Option value="5 years">5 years</Option>
                    <Option value="6 years">6 years</Option>
                    <Option value="7 years">7 years</Option>
                    <Option value="8 years">8 years</Option>
                    <Option value="9 years">9 years</Option>
                    <Option value="10 years">10 years</Option>
                    <Option value="11 years">11 years</Option>
                    <Option value="12 years">12 years</Option>
                    <Option value="13 years">13 years</Option>
                    <Option value="14 years">14 years</Option>
                    <Option value="15 years">15 years</Option>
                    <Option value="16 years">16 years</Option>
                    <Option value="17 years">17 years</Option>
                    <Option value="18 years">18 years</Option>
                    <Option value="19 years">19 years</Option>
                    <Option value="20 years">20 years</Option>
                    <Option value="21 years">21 years</Option>
                    <Option value="22 years">22 years</Option>
                    <Option value="23 years">23 years</Option>
                    <Option value="24 years">24 years</Option>
                    <Option value="25 years">25 years</Option>
                    <Option value="26 years">26 years</Option>
                    <Option value="27 years">27 years</Option>
                    <Option value="28 years">28 years</Option>
                    <Option value="29 years">29 years</Option>
                    <Option value="30 years">30 years</Option>
                    <Option value=">30 years">Above 30 years</Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  name="gender"
                  label="Gender"
                  className="profile-section-title"
                  rules={[
                    {
                      required: true,
                      message: "Please select an gender",
                    },
                  ]}
                  initialValue={data?.gender}
                >
                  <Select placeholder="Please select an gender" initialValue={data?.gender}>
                    <Option value="male">Male</Option>
                    <Option value="female">Female</Option>
                  </Select>
                </Form.Item>
              </Col>

              {/* <Col span={12}>
                <Form.Item
                  name="teching_type"
                  label="Teaching Type"
                  className="profile-section-title"
                  rules={[
                    {
                      required: true,
                      message: "Please select an teaching type",
                    },
                  ]}
                  initialValue={data?.teching_type}
                >
                  <Select
                    placeholder="Please select an teaching type"
                    initialValue={data?.teching_type}
                  >
                    <Option value="Looking for Part Time Tuitions">
                      Looking for Part Time Tuitions
                    </Option>
                    <Option value="Looking for Full Time Tuitions">
                      Looking for Full Time Tuitions
                    </Option>
                    <Option value="Both">Both</Option>
                  </Select>
                </Form.Item>
              </Col> */}
              <Col span={12}>
                <Form.Item
                  name="have_vehicle"
                  label="Have Vehicle"
                  className="profile-section-title"
                  rules={[
                    {
                      required: true,
                      message: "Please choose have vehicle ",
                    },
                  ]}
                  initialValue={data?.have_vehicle}
                >
                  <Select placeholder="Please choose have vehicle" initialValue={data?.have_vehicle}>
                    <Option value={0}>No</Option>
                    <Option value={1}>Yes</Option>
                  </Select>
                </Form.Item>
              </Col>
              {(role === "Admin" || role === "Superadmin") && (
                <Col span={12}>
                  <Form.Item
                    name="is_approved"
                    label="Verify Status"
                    className="profile-section-title"
                    rules={[
                      {
                        required: true,
                        message: "Please choose Approve Status ",
                      },
                    ]}
                    initialValue={data?.is_approved}
                  >
                    <Select placeholder="Please choose Approve Status" initialValue={data?.is_approved}>
                      <Option value={0}>Not Verified</Option>
                      <Option value={1}>Verified</Option>
                    </Select>
                  </Form.Item>
                </Col>
              )}
              {/* <Col span={12}>
                <Form.Item
                  name="is_paid"
                  label="Payment Status"
                  className="profile-section-title"
                  rules={[
                    {
                      required: true,
                      message: "Please choose Payment Status ",
                    },
                  ]}
                  initialValue={data?.is_paid}
                >
                  <Select
                    placeholder="Please choose Payment Status"
                    initialValue={data?.is_paid}
                  >
                    <Option value={0}>Not Paid</Option>
                    <Option value={1}>Paid</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="city_id"
                  label="City"
                  className="profile-section-title"
                  rules={[
                    {
                      required: true,
                      message: "Please choose City ",
                    },
                  ]}
                  initialValue={citys[data?.city_id]}
                >
                  <Select
                    placeholder="Please choose City"
                    initialValue={citys[data?.city_id]}
                  >
                    {Object.keys(citys).map((cityId) => {
                      return <Option value={cityId}>{citys[cityId]}</Option>;
                    })}
                  </Select>
                </Form.Item>
              </Col> */}
            </Row>
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  name="cre_comment"
                  className="profile-section-title"
                  label="Comment"
                  rules={[
                    {
                      required: false,
                      message: "please enter comment",
                    },
                  ]}
                  initialValue={data?.cre_comment}
                >
                  <Input.TextArea initialValue={data?.cre_comment} rows={4} placeholder="please enter comment" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              wrapperCol={{
                offset: 8,
                span: 16,
              }}
            >
              <Space>
                <Button type="primary" htmlType="submit">
                  Submit
                </Button>
                <Button
                  htmlType="button"
                  onClick={() => {
                    setActiveEdit("");
                  }}
                >
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        )}

        {/* ---------------------------------------- other details end-------------------------------- */}
        {/* ---------------------------------------- Document details start-------------------------------- */}

        <section className="section-title-edit-wrapper">
          <div className="section-title-container">
            Document Details
            <div
              style={{
                backgroundColor: "rgba(44, 153, 27, 1)",
                borderRadius: "50%",
                width: "20px",
                height: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginLeft: "7px",
              }}
            >
              <img src="/white-tick.svg" alt="tick mark" />
            </div>
          </div>
          {/* {!activeEdit && (
            <div className="tail-flex tail-items-end tail-gap-2">
              <Button
                type="primary"
                className="tail-mt-1"
                icon={<EditOutlined />}
                iconPosition="end"
                size="medium"
                onClick={() => {
                  setActiveEdit("rating-details");
                  setActiveEdit("document-details");
                }}
              >
                Edit
              </Button>
            </div>
          )} */}
        </section>

        {activeEdit !== "document-details" && (
          <section className="documents-section">
            <div className="profile-section-personal-details-titles-wrapper">
              <span className="profile-section-title">
                ID Proof-1
                <br />
                <label htmlFor="adhaar" className="file-upload-btn">
                  upload
                </label>
              </span>
              <span className="profile-section-value">
                {" "}
                : <ShowDoc src={tutorDocs?.adhaar} onDelete={deleteDoc} docType="adhaar" />
                <ShowUploadOption id="adhaar" />
              </span>
            </div>
            <div className="profile-section-personal-details-titles-wrapper">
              <span className="profile-section-title">
                ID Proof-2
                <br />
                <label htmlFor="id_card" className="file-upload-btn">
                  upload
                </label>
              </span>
              <span className="profile-section-value">
                : <ShowDoc src={tutorDocs?.id_card} onDelete={deleteDoc} docType="id_card" />
                <ShowUploadOption id="id_card" />
              </span>
            </div>
            <div className="profile-section-personal-details-titles-wrapper">
              <span className="profile-section-title">
                CV Doc
                <br />
                <label htmlFor="cv_file" className="file-upload-btn">
                  upload
                </label>
              </span>
              <span className="profile-section-value">
                : <ShowDoc src={tutorDocs?.cv_file} onDelete={deleteDoc} docType="cv_file" />
                <ShowUploadOption id="cv_file" />
              </span>
            </div>
            <div className="profile-section-personal-details-titles-wrapper">
              <span className="profile-section-title">
                Resume
                <br />
                <label htmlFor="resume_file" className="file-upload-btn">
                  upload
                </label>
              </span>
              <span className="profile-section-value">
                : <ShowDoc src={tutorDocs?.resume_file} onDelete={deleteDoc} docType="resume_file" />
                <ShowUploadOption id="resume_file" />
              </span>
            </div>
            {/* <div className="profile-section-personal-details-titles-wrapper">
              <span className="profile-section-title">Intro Video : </span>
              <span className="profile-section-value">
                <Image
                  width={150}
                  src={tutorDocs?.into_video}
                  fallback="./person-icon.png"
                />
              </span>
            </div> */}
          </section>
        )}

        {/* ---------------------------------------- Document details end-------------------------------- */}
        {/* ---------------------------------------- Payments details Start-------------------------------- */}

        {/* <section className="section-title-edit-wrapper">
          <div className="section-title-container">Paymnets Details</div>
          {activeEdit === "" && (
            <div
              className="section-edit-container"
            >
            </div>
          )}
        </section> */}

        {/* ---------------------------------------- Payments details End-------------------------------- */}

        {/* {JSON.stringify(data)} */}

        <section className="section-title-edit-wrapper">
          <div className="section-title-container">
            Courses Details
            <div
              style={{
                backgroundColor: "rgba(44, 153, 27, 1)",
                borderRadius: "50%",
                width: "20px",
                height: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginLeft: "7px",
              }}
            >
              <img src="/white-tick.svg" alt="tick mark" />
            </div>
          </div>

          {step === 3 ? (
            <div className="tail-flex tail-items-end tail-gap-2">
              <Button
                type="primary"
                className="tail-mt-1"
                icon={<EditOutlined />}
                iconPosition="end"
                size="medium"
                onClick={() => {
                  setStep(0);
                }}
              >
                Edit
              </Button>
            </div>
          ) : (
            <div className="tail-flex tail-items-end tail-gap-2">
              <Button
                danger
                className="tail-mt-1"
                icon={<ClearOutlined />}
                iconPosition="end"
                size="medium"
                onClick={() => {
                  fetchAndSetAllCourses();
                }}
              >
                Cancel
              </Button>
            </div>
          )}
        </section>

        {fullCourseTemplate.length !== 0 && (
          <MyCourses
            step={step}
            setStep={setStep}
            fullCourseTemplate={fullCourseTemplate}
            setFullCourseTemplate={setFullCourseTemplate}
            saveTutorCourses={saveTutorCourses}
          />
        )}

        <section className="section-title-edit-wrapper">
          <div className="section-title-container">Reports</div>
        </section>
        <section>
          <TutorHistory tutor_id={id} />
        </section>
        <section className="section-title-edit-wrapper">
          <div className="section-title-container">Subscription History</div>
        </section>
        <section>
          <TutorSubscriptionHistory tutorId={data?.user_id} />
        </section>

        {/* ===== Payment / Bank Details Section ===== */}
        <section className="section-title-edit-wrapper">
          <div className="section-title-container">Payment / Bank Details</div>
        </section>
        <section style={{ padding: "16px 24px" }}>
          <Spin spinning={loadingBankDetails} tip="Loading...">
            {/* Bank Details */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <h4 style={{ margin: 0, fontWeight: 600 }}>Bank Details</h4>
                {!isEditingBankDetails && (
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    size="small"
                    onClick={() => setIsEditingBankDetails(true)}
                  >
                    {bankDetails.accountHolderName ? "Edit" : "Add"}
                  </Button>
                )}
              </div>

              {isEditingBankDetails ? (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  {[
                    { key: "accountHolderName", label: "Account Holder Name", required: true },
                    { key: "bankName", label: "Bank Name" },
                    { key: "accountNumber", label: "Account Number", required: true },
                    { key: "ifscCode", label: "IFSC Code", required: true },
                    { key: "panNumber", label: "PAN Number" },
                    { key: "upiId", label: "UPI ID" },
                  ].map(({ key, label, required }) => (
                    <div key={key}>
                      <label style={{ fontSize: 12, color: "#888", display: "block", marginBottom: 4 }}>
                        {label}{required ? " *" : ""}
                      </label>
                      <Input
                        placeholder={label}
                        value={bankDetailsDraft[key]}
                        onChange={(e) => setBankDetailsDraft((p) => ({ ...p, [key]: e.target.value }))}
                      />
                    </div>
                  ))}
                  <div style={{ gridColumn: "1 / -1", display: "flex", gap: 8, marginTop: 4 }}>
                    <Button
                      type="primary"
                      icon={<SaveOutlined />}
                      onClick={handleSaveBankDetails}
                      loading={savingBankDetails}
                    >
                      Save Bank Details
                    </Button>
                    <Button
                      onClick={() => {
                        setIsEditingBankDetails(false);
                        setBankDetailsDraft({ ...bankDetails });
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : bankDetails.accountHolderName ? (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
                  {[
                    { label: "Holder Name", value: bankDetails.accountHolderName },
                    { label: "Bank", value: bankDetails.bankName || "-" },
                    { label: "A/C Number", value: bankDetails.accountNumber ? "****" + bankDetails.accountNumber.slice(-4) : "-" },
                    { label: "IFSC", value: bankDetails.ifscCode || "-" },
                    { label: "PAN", value: bankDetails.panNumber || "-" },
                    { label: "UPI", value: bankDetails.upiId || "-" },
                  ].map(({ label, value }) => (
                    <div key={label}>
                      <p style={{ margin: 0, color: "#888", fontSize: 12 }}>{label}</p>
                      <p style={{ margin: 0, fontWeight: 500, fontSize: 13 }}>{value}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <Alert message="No bank details found. Click 'Add' to add bank details." type="warning" showIcon />
              )}
            </div>

            {/* Razorpay Account */}
            <div>
              <h4 style={{ margin: "0 0 12px", fontWeight: 600 }}>Razorpay Account</h4>
              {razorpayLinkedAccountId ? (
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <Tag color={razorpayAccountStatus === "active" ? "green" : "orange"} style={{ fontSize: 12 }}>
                    {razorpayAccountStatus || "created"}
                  </Tag>
                  <span style={{ fontSize: 13, color: "#666" }}>
                    Account ID: <strong>{razorpayLinkedAccountId}</strong>
                  </span>
                </div>
              ) : (
                <div>
                  <Alert
                    message="No Razorpay linked account found"
                    description={
                      bankDetails.accountHolderName && bankDetails.accountNumber && bankDetails.ifscCode
                        ? "Create a Razorpay linked account to enable automated transfers to this tutor."
                        : "Add bank details first to create a Razorpay linked account."
                    }
                    type="info"
                    showIcon
                    style={{ marginBottom: 8 }}
                  />
                  <Button
                    type="primary"
                    onClick={handleCreateRazorpayAccount}
                    loading={creatingRazorpayAccount}
                    disabled={!bankDetails.accountHolderName || !bankDetails.accountNumber || !bankDetails.ifscCode}
                  >
                    Create Razorpay Account
                  </Button>
                </div>
              )}
            </div>
          </Spin>
        </section>

        <section className="section-title-edit-wrapper">
          <div className="section-title-container">Earnings Details</div>
        </section>

        <section className="earnings-summary-card">
          <Spin spinning={loadingEarnings}>
            <div className="earnings-summary-values">
              <span>Total earnings: {formatINR(earningsSummary.total)}</span>
              <span className="earnings-divider">|</span>
              <span>Last month earnings: {formatINR(earningsSummary.lastMonth)}</span>
            </div>
            <Button
              type="primary"
              onClick={() => {
                navigate(`/tutor/${id}/earnings-report`);
              }}
            >
              View Details
            </Button>
          </Spin>
        </section>

        {/* Payment Type Selection Modal */}
        <Modal
          title="Select Payment Plan Type"
          open={isPaymentTypeModalOpen}
          onOk={handlePaymentTypeModalOk}
          onCancel={() => {
            setIsPaymentTypeModalOpen(false);
            setSelectedPaymentType("basic");
            setPaymentTypeModalContext("");
          }}
          okText="Proceed"
          cancelText="Cancel"
          confirmLoading={confirmLoading}
        >
          <div style={{ padding: "20px 0" }}>
            <Radio.Group
              onChange={(e) => setSelectedPaymentType(e.target.value)}
              value={selectedPaymentType}
              style={{ display: "flex", flexDirection: "column", gap: "15px" }}
            >
              <Radio value="Basic" style={{ fontSize: "16px" }}>
                Basic Plan
              </Radio>
              <Radio value="Premium" style={{ fontSize: "16px" }}>
                Premium Plan
              </Radio>
            </Radio.Group>
          </div>
        </Modal>
      </div>
    </>
  );
};
export default TutorDrawer;
